import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';

const REASSURANCE_MESSAGES = [
  "Preparing your will with care…",
  "You're creating clarity and peace of mind for the people you love.",
  "Preserving your wishes safely for the future.",
  "Reviewing legal details to ensure accuracy and completeness.",
  "Running essential validation checks.",
  "Securing and encrypting your data."
];

const MESSAGE_DURATION = 800; // ~0.8 seconds per message
const FADE_DURATION = 300; // 300ms fade transition

const WillProcessingModal = ({ isOpen, onComplete }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentMessageIndex(0);
      setIsVisible(true);
      setIsExiting(false);
      return;
    }

    // Cycle through messages
    const messageInterval = setInterval(() => {
      setIsVisible(false); // Start fade out
      
      setTimeout(() => {
        setCurrentMessageIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= REASSURANCE_MESSAGES.length) {
            // Last message - trigger exit animation
            clearInterval(messageInterval);
            setTimeout(() => {
              setIsExiting(true);
              setTimeout(() => {
                onComplete();
              }, 500); // Wait for exit animation
            }, MESSAGE_DURATION);
            return prev;
          }
          return nextIndex;
        });
        setIsVisible(true); // Fade in new message
      }, FADE_DURATION);
    }, MESSAGE_DURATION + FADE_DURATION);

    return () => clearInterval(messageInterval);
  }, [isOpen, onComplete]);

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-500 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="will-processing-modal"
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-slate-950/92 backdrop-blur-md" />
      
      {/* Content Card */}
      <div 
        className={`relative z-10 w-full max-w-md mx-4 transform transition-all duration-500 ${
          isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400" />
          
          <div className="px-8 py-10 sm:px-10 sm:py-12">
            {/* Animated Shield Icon */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {/* Outer glow ring */}
                <div className="absolute inset-0 rounded-full bg-amber-400/20 animate-pulse-slow" 
                     style={{ transform: 'scale(1.6)' }} />
                
                {/* Inner pulsing circle */}
                <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center shadow-lg">
                  <div className="absolute inset-2 rounded-full bg-white/80 backdrop-blur-sm" />
                  <Shield 
                    className="relative z-10 w-10 h-10 text-amber-500 animate-shield-pulse" 
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            </div>
            
            {/* Message Container */}
            <div className="text-center min-h-[72px] flex items-center justify-center">
              <p 
                className={`text-slate-700 text-lg sm:text-xl font-medium leading-relaxed transition-opacity duration-300 ${
                  isVisible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {REASSURANCE_MESSAGES[currentMessageIndex]}
              </p>
            </div>
            
            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {REASSURANCE_MESSAGES.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentMessageIndex 
                      ? 'bg-amber-500 w-3' 
                      : index < currentMessageIndex 
                        ? 'bg-amber-300' 
                        : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Bottom subtle branding */}
          <div className="bg-slate-50 px-8 py-4 border-t border-slate-100">
            <p className="text-center text-slate-400 text-xs tracking-wide uppercase">
              Securing Your Legacy
            </p>
          </div>
        </div>
      </div>
      
      {/* Custom keyframes via style tag */}
      <style>{`
        @keyframes shield-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1.6); opacity: 0.3; }
          50% { transform: scale(1.8); opacity: 0.15; }
        }
        
        .animate-shield-pulse {
          animation: shield-pulse 2s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default WillProcessingModal;
